import { Blocs } from '@business/blocs';
import { SupportTicketAPI } from '@business/api/SupportTicket';
import {
  CompactSupportTicketResponse,
  SupportTicketActionName,
  SupportTicketStatus,
  TicketAdditionalResponse,
  TicketId,
  TicketNotification,
  TicketSearchParams,
} from '@business/entities/SupportTicket';
import { SupportTicketStatusResponse } from '@business/entities/support_ticket_status';
import { BaseBloc } from '@core/utils/bloc';
import { resolve } from '@core/utils/ioc';
import { PaginatedController } from '@core/utils/paginated';
import { R } from '@core/utils/r';
import { LoadingState } from '@core/utils/repository/loading_state';
import { Storage } from '@core/utils/storage';
import { Session } from '@modules/auth/session';
import { forkJoin, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

export const SUPPORT_INITIAL_QUERY: TicketSearchParams = {
  channelId: '1',
  page: 1,
  search: '',
  userId: null,
  status: null,
  sortBy: 'lastActivityTime',
  sortDirection: 'desc',
};

export const SUPPORT_STORAGE_KEY = `support:query:${Session.user?.id || '0'}`;
export class TicketsPageBloc extends BaseBloc {
  private readonly supportBloc = resolve(Blocs.supportTicket);
  private readonly userBloc = resolve(Blocs.user);
  private readonly channelBloc = resolve(Blocs.supportChannel);
  private readonly categoryBloc = resolve(Blocs.supportCategory);
  private readonly echoBloc = resolve(Blocs.echo);
  private readonly supportTicketStatusBloc = resolve(Blocs.supportTicketStatus);

  loading = new LoadingState();

  readonly channels$ = this.channelBloc.dChannels$;
  readonly users$ = this.userBloc.staff$;
  readonly categories$ = this.categoryBloc.categories$;
  readonly supportTicketStatuses$ = this.supportTicketStatusBloc.statuses$;
  readonly notifications$ = this.echoBloc.subscribe<TicketNotification>('support');

  readonly totals$ = this.channels$.pipe(
    map(channels => {
      const total = channels.reduce((sum, item) => sum + item.totalTickets, 0);
      const opened = channels.reduce((sum, item) => sum + item.openTickets, 0);
      const todayOpened = channels.reduce((sum, item) => sum + item.todayOpened, 0);
      const todayClosed = channels.reduce((sum, item) => sum + item.todayClosed, 0);
      return { total, opened, todayOpened, todayClosed };
    }),
  );

  onInit() {
    return forkJoin([
      this.channelBloc.fetchDetailChannels(),
      this.categoryBloc.fetchCategories(),
      this.userBloc.fetchUsers(),
      this.supportTicketStatusBloc.fetchSupportTicketStatuses(),
    ]).pipe(
      switchMap(([channels]) => {
        const currentChannelId = this.paginated.pageQuery$.value.channelId;
        const channel = R.find(channels, c => c.id === currentChannelId) || R.first(channels);
        if (channel) {
          return this.paginated.patchQuery({ channelId: channel.id, tabId: channel.tabs[0]!.tabId }, true);
        }
        return of(null);
      }),
      this.loading.run(),
    );
  }

  onDestroy() {
    this.echoBloc.unsubscribe('support');
  }

  fetchChannels = this.channelBloc.fetchChannels;

  fetchTickets = (query: TicketSearchParams) => {
    Storage.set(SUPPORT_STORAGE_KEY, query);
    this.channelBloc.fetchDetailChannels().subscribe();
    return this.supportBloc.fetchTickets(query).pipe(this.loading.run({}));
  };

  readonly paginated = new PaginatedController<CompactSupportTicketResponse, string, TicketSearchParams, TicketAdditionalResponse>(
    this.fetchTickets,
    '',
    () => Storage.get<TicketSearchParams>(SUPPORT_STORAGE_KEY, SUPPORT_INITIAL_QUERY),
    // SUPPORT_INITIAL_QUERY,
  );

  readonly tickets$ = this.paginated.items$;
  readonly query$ = this.paginated.pageQuery$;

  updateTicketAction = this.supportBloc.updateTicketAction;
  updatePriority = (id: TicketId, action: SupportTicketActionName, actionId: string) => {
    return this.supportBloc.updateTicketAction(id, action, actionId, true).pipe(tap(item => this.paginated.updateItem(item, 'id')));
  };

  bulkAction = (ids: TicketId[], status: SupportTicketStatus, closeAllTasks?: boolean) => {
    return this.supportBloc.bulkAction(ids, status, closeAllTasks).pipe(switchMap(() => this.paginated.reload()));
  };

  createSupportTicketStatus = (data: Partial<SupportTicketStatusResponse>) => {
    this.supportTicketStatusBloc.createSupportTicketStatus(data).subscribe();
  };
  saveManualOrder = SupportTicketAPI.saveTicketOrder;
}
